// src/services/chatQuickActions.js
// Pre-built frontend functions that the chat agent calls directly
// for common queries — instant responses without AI round-trip.
//
// IMPORTANT: The search_uhid backend deduplicates to ONE record per UHID
// (IPD/EMERGENCY > OPD > LAB), so the LAB/IPD record itself may be dropped.
// BUT every returned record carries per-UHID visit counts that survive the
// dedup: lab_visit_count, ipd_visit_count, opd_visit_count, pharma_visit_count.
// Department status is therefore derived from THOSE COUNTS, not lab_reg_type.

import { apiClient } from "./api";
import AppConst from "../apis/appconst";

/* ------------------------------------------------------------------ */
/* Request helpers                                                     */
/* ------------------------------------------------------------------ */

const postForm = async (endpointKey, fields = {}) => {
  const fd = new FormData();
  Object.entries(fields).forEach(([key, val]) => {
    if (val !== undefined && val !== null) fd.append(key, String(val));
  });
  const resp = await apiClient.post(endpointKey, fd, {
    headers: { "Content-Type": "multipart/form-data" },
    timeout: 15000,
  });
  return resp.data;
};

const getReq = async (endpointKey) => {
  const resp = await apiClient.get(endpointKey, {
    headers: { "Content-Type": "application/json" },
    timeout: 15000,
  });
  return resp.data;
};

/* ------------------------------------------------------------------ */
/* Small utilities                                                     */
/* ------------------------------------------------------------------ */

export const getCurrentUser = () => {
  try {
    const raw = sessionStorage.getItem("ldata");
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

const todayStr = () => {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

const extractList = (data) =>
  data?.records || data?.data || data?.list || data?.patients ||
  (Array.isArray(data) ? data : []) || [];

const normUhid = (v) => String(v ?? "").trim().toUpperCase().replace(/\s+/g, "");

const recUhid = (p) =>
  p?.lab_reg_uhid || p?.uhid || p?.opd_uhid || p?.ipd_uhid || p?.patient_id || "";
const recName = (p) =>
  p?.lab_reg_name || p?.patient_name || p?.name || "—";
const recType = (p) => String(p?.lab_reg_type || "").toUpperCase();

// Numeric visit counts (present on every deduped record)
const labCount = (p) => Number(p?.lab_visit_count ?? 0);
const ipdCount = (p) => Number(p?.ipd_visit_count ?? 0);
const opdCount = (p) => Number(p?.opd_visit_count ?? 0);
const pharmaCount = (p) => Number(p?.pharma_visit_count ?? 0);

// Build a "Found In" label from the counts
const departmentsFromCounts = (p) => {
  const parts = [];
  if (opdCount(p) > 0) parts.push("OPD");
  if (ipdCount(p) > 0) parts.push("IPD");
  if (labCount(p) > 0) parts.push("LAB");
  if (pharmaCount(p) > 0) parts.push("PHARMACY");
  return parts.join(", ");
};

/* ------------------------------------------------------------------ */
/* Dashboard                                                           */
/* ------------------------------------------------------------------ */

export const getDashboardStats = async () => {
  try {
    const data = await getReq(AppConst.GetDashboard);
    if (data && (data.status || data.ok || data.success)) {
      const stats = data.stats || data.data || data;
      return {
        type: "stats",
        title: "📊 Today's Dashboard",
        cards: [
          { label: "OPD Patients", value: stats.opd_count ?? stats.total_opd ?? "—" },
          { label: "IPD Admissions", value: stats.ipd_count ?? stats.total_ipd ?? "—" },
          { label: "Lab Registrations", value: stats.lab_count ?? stats.total_lab ?? "—" },
          { label: "Pharmacy Sales", value: stats.pharmacy_count ?? stats.total_pharmacy ?? "—" },
          { label: "Total Collection", value: stats.total_collection ? `₹${Number(stats.total_collection).toLocaleString("en-IN")}` : "—" },
          { label: "Pending Reports", value: stats.pending_reports ?? "—" },
        ].filter(c => c.value !== "—"),
        text: "Here are today's live stats from your dashboard."
      };
    }
    return null;
  } catch (err) {
    console.warn("[QuickAction] getDashboardStats error:", err.message);
    return null;
  }
};

/* ------------------------------------------------------------------ */
/* OPD / IPD / Beds / Lab-reports lists                                */
/* ------------------------------------------------------------------ */

export const getOpdPatients = async () => {
  try {
    const today = todayStr();
    const data = await postForm(AppConst.getOpdListUrl, {
      page_no: "none", search_key: "",
      from: today + " 00:00:00", to: today + " 23:59:59",
    });
    const list = extractList(data);
    if (list.length === 0) return { type: "text", text: "No OPD patients found for today." };
    return {
      type: "table",
      title: `🏥 OPD Patients (${list.length} today)`,
      columns: ["#", "Patient", "UHID", "Doctor", "Status"],
      rows: list.slice(0, 20).map((p, i) => [
        i + 1,
        p.opd_patient_name || recName(p),
        p.opd_uhid || recUhid(p) || "—",
        p.doctor_name || p.opd_doctor_name || p.doctor || "—",
        p.opd_status || p.status || "—"
      ]),
      text: list.length > 20 ? `Showing first 20 of ${list.length} patients.` : ""
    };
  } catch (err) {
    console.warn("[QuickAction] getOpdPatients error:", err.message);
    return null;
  }
};

export const getIpdPatients = async () => {
  try {
    const data = await postForm(AppConst.getIpdListUrl, { page_no: "none", search_key: "" });
    const list = extractList(data);
    if (list.length === 0) return { type: "text", text: "No current IPD admissions found." };
    return {
      type: "table",
      title: `🏨 IPD Patients (${list.length} admitted)`,
      columns: ["#", "Patient", "UHID", "Ward/Bed", "Doctor", "Admitted"],
      rows: list.slice(0, 20).map((p, i) => [
        i + 1,
        p.ipd_patient_name || recName(p),
        p.ipd_uhid || recUhid(p) || "—",
        `${p.ward_name || p.ipd_ward || "—"} / ${p.bed_name || p.ipd_bed || p.bed_no || "—"}`,
        p.doctor_name || p.ipd_doctor_name || p.doctor || "—",
        p.admission_date || p.ipd_reg_date || p.ipd_date || p.created_at || "—"
      ]),
      text: list.length > 20 ? `Showing first 20 of ${list.length} patients.` : ""
    };
  } catch (err) {
    console.warn("[QuickAction] getIpdPatients error:", err.message);
    return null;
  }
};

export const getAvailableBeds = async () => {
  try {
    const data = await postForm(AppConst.getBedListUrl, { page_no: "none", search_key: "" });
    const list = extractList(data);
    const available = list.filter(b =>
      !b.is_occupied && !b.patient_name &&
      (b.bed_status === "available" || b.bed_status === "Available" || b.status === "available" || !b.bed_patient)
    );
    const occupied = list.length - available.length;
    return {
      type: "stats",
      title: "🛏️ Bed Availability",
      cards: [
        { label: "Total Beds", value: list.length },
        { label: "Available", value: available.length },
        { label: "Occupied", value: occupied },
        { label: "Occupancy %", value: list.length ? Math.round((occupied / list.length) * 100) + "%" : "—" },
      ],
      text: available.length > 0
        ? `${available.length} beds are currently available.`
        : "All beds are currently occupied."
    };
  } catch (err) {
    console.warn("[QuickAction] getAvailableBeds error:", err.message);
    return null;
  }
};

export const getPendingLabReports = async () => {
  try {
    const today = todayStr();
    const data = await postForm(AppConst.getLabRegListUrl, {
      page_no: "none", search_key: "", lab_reg_user: "all",
      from: today + " 00:00:00", to: today + " 23:59:59",
    });
    const list = extractList(data);
    const pending = list.filter(r =>
      r.lab_reg_status === "pending" || r.lab_reg_status === "Pending" ||
      r.status === "pending" || r.report_status === "pending"
    );
    if (list.length === 0) return { type: "text", text: "No lab registrations found for today." };
    return {
      type: "stats",
      title: "🧪 Lab Reports Status (Today)",
      cards: [
        { label: "Total Registrations", value: list.length },
        { label: "Pending", value: pending.length },
        { label: "Completed", value: list.length - pending.length },
      ],
      text: pending.length > 0
        ? `${pending.length} lab reports are pending completion.`
        : "All lab reports for today are completed! ✅"
    };
  } catch (err) {
    console.warn("[QuickAction] getPendingLabReports error:", err.message);
    return null;
  }
};

/* ------------------------------------------------------------------ */
/* Patient search — sends lab_reg_uhid (the field the backend reads)   */
/* ------------------------------------------------------------------ */

export const searchPatientRaw = async (uhidOrName, kind = "uhid") => {
  const fields = {};
  if (kind === "uhid")   fields.lab_reg_uhid = uhidOrName;
  if (kind === "name")   fields.lab_reg_name = uhidOrName;
  if (kind === "mobile") fields.lab_reg_mobile = uhidOrName;
  fields.search = uhidOrName;
  fields.search_key = uhidOrName;
  const data = await postForm(AppConst.searchUhidUrlCall, fields);
  return extractList(data);
};

export const searchPatient = async (query, kind = "uhid") => {
  try {
    const results = await searchPatientRaw(query, kind);
    if (results.length === 0) {
      return { type: "text", text: `No patient found matching "${query}".` };
    }
    if (results.length === 1) {
      const p = results[0];
      const depts = departmentsFromCounts(p) || recType(p) || "—";
      return {
        type: "info",
        title: `👤 ${recName(p)}`,
        rows: [
          { label: "UHID", value: recUhid(p) || "—" },
          { label: "Found In", value: depts },
          { label: "Visits", value: `OPD ${opdCount(p)} · IPD ${ipdCount(p)} · Lab ${labCount(p)}` },
          { label: "Age", value: p.lab_age || p.lab_reg_age_year || "—" },
          { label: "Gender", value: p.lab_reg_gender || p.gender || "—" },
          { label: "Mobile", value: p.lab_reg_mobile || p.mobile || p.phone || "—" },
        ].filter(r => r.value !== "—"),
        text: ""
      };
    }
    return {
      type: "table",
      title: `🔍 Search Results (${results.length} found)`,
      columns: ["#", "Name", "UHID", "Found In", "Mobile"],
      rows: results.slice(0, 15).map((p, i) => [
        i + 1,
        recName(p),
        recUhid(p) || "—",
        departmentsFromCounts(p) || recType(p) || "—",
        p.lab_reg_mobile || p.mobile || p.phone || "—"
      ]),
      text: results.length > 15 ? `Showing first 15 of ${results.length} results.` : ""
    };
  } catch (err) {
    console.warn("[QuickAction] searchPatient error:", err.message);
    return null;
  }
};

/* ------------------------------------------------------------------ */
/* LIVE STATUS — derived from visit counts (survive backend dedup)     */
/* ------------------------------------------------------------------ */

// Find the record matching the requested UHID from the search results.
const findRecord = (results, uhid) => {
  const target = normUhid(uhid);
  return results.find(r => normUhid(recUhid(r)) === target) || results[0] || null;
};

// "Is this UHID in IPD?"  → ipd_visit_count > 0
export const getPatientStatus = async (uhid) => {
  try {
    const target = normUhid(uhid);
    if (!target) return { type: "text", text: "Please provide a valid UHID to check." };

    const results = await searchPatientRaw(uhid, "uhid").catch(() => []);
    if (results.length === 0) {
      return { type: "text", text: `No patient found for UHID "${uhid}". Double-check the UHID.` };
    }
    const rec = findRecord(results, uhid);
    const name = recName(rec);
    const depts = departmentsFromCounts(rec) || "—";

    if (ipdCount(rec) > 0) {
      return {
        type: "info",
        title: `🏨 ${name} — In IPD`,
        rows: [
          { label: "UHID", value: recUhid(rec) || uhid },
          { label: "Status", value: "✅ Has IPD admission(s)" },
          { label: "IPD Visits", value: ipdCount(rec) },
          { label: "Bed", value: rec.lab_bed || rec.ipd_bed || "—" },
          { label: "Found In", value: depts },
        ].filter(r => r.value !== "—" && r.value !== undefined),
        text: `Yes — ${name} (${uhid}) has ${ipdCount(rec)} IPD record(s).`
      };
    }
    return {
      type: "info",
      title: `📋 ${name} — Not in IPD`,
      rows: [
        { label: "UHID", value: recUhid(rec) || uhid },
        { label: "Status", value: "🚫 No IPD admission" },
        { label: "Found In", value: depts },
      ].filter(r => r.value !== "—"),
      text: `${name} (${uhid}) has no IPD admission. Found in: ${depts}.`
    };
  } catch (err) {
    console.warn("[QuickAction] getPatientStatus error:", err.message);
    return null;
  }
};

// "Is this UHID in the lab?"  → lab_visit_count > 0
export const getLabStatus = async (uhid) => {
  try {
    const target = normUhid(uhid);
    if (!target) return { type: "text", text: "Please provide a valid UHID to check." };

    const results = await searchPatientRaw(uhid, "uhid").catch(() => []);
    if (results.length === 0) {
      return { type: "text", text: `No patient found for UHID "${uhid}". Double-check the UHID.` };
    }
    const rec = findRecord(results, uhid);
    const name = recName(rec);
    const depts = departmentsFromCounts(rec) || "—";

    if (labCount(rec) > 0) {
      return {
        type: "info",
        title: `🧪 ${name} — In Lab`,
        rows: [
          { label: "UHID", value: recUhid(rec) || uhid },
          { label: "Status", value: "✅ Registered in Lab" },
          { label: "Lab Visits", value: labCount(rec) },
          { label: "Found In", value: depts },
        ].filter(r => r.value !== "—" && r.value !== undefined),
        text: `Yes — ${name} (${uhid}) has ${labCount(rec)} lab registration(s).`
      };
    }
    return {
      type: "info",
      title: `📋 ${name} — Not in Lab`,
      rows: [
        { label: "UHID", value: recUhid(rec) || uhid },
        { label: "Status", value: "🚫 No lab registration" },
        { label: "Found In", value: depts },
      ].filter(r => r.value !== "—"),
      text: `${name} (${uhid}) has no lab registration. Found in: ${depts}.`
    };
  } catch (err) {
    console.warn("[QuickAction] getLabStatus error:", err.message);
    return null;
  }
};

// "Is this UHID in OPD?"  → opd_visit_count > 0  (bonus, same pattern)
export const getOpdStatus = async (uhid) => {
  try {
    const target = normUhid(uhid);
    if (!target) return { type: "text", text: "Please provide a valid UHID to check." };
    const results = await searchPatientRaw(uhid, "uhid").catch(() => []);
    if (results.length === 0) {
      return { type: "text", text: `No patient found for UHID "${uhid}".` };
    }
    const rec = findRecord(results, uhid);
    const name = recName(rec);
    const depts = departmentsFromCounts(rec) || "—";
    const inOpd = opdCount(rec) > 0;
    return {
      type: "info",
      title: inOpd ? `🏥 ${name} — In OPD` : `📋 ${name} — Not in OPD`,
      rows: [
        { label: "UHID", value: recUhid(rec) || uhid },
        { label: "Status", value: inOpd ? "✅ Has OPD visit(s)" : "🚫 No OPD visit" },
        { label: "OPD Visits", value: opdCount(rec) },
        { label: "Found In", value: depts },
      ].filter(r => r.value !== "—" && r.value !== undefined),
      text: inOpd
        ? `Yes — ${name} (${uhid}) has ${opdCount(rec)} OPD visit(s).`
        : `${name} (${uhid}) has no OPD visit. Found in: ${depts}.`
    };
  } catch (err) {
    console.warn("[QuickAction] getOpdStatus error:", err.message);
    return null;
  }
};

/* ------------------------------------------------------------------ */
/* Collections / medicines / doctors                                   */
/* ------------------------------------------------------------------ */

export const getTodayCollections = async () => {
  try {
    const today = todayStr();
    const data = await postForm(AppConst.getLabAccountsSummaryUrl, { from: today, to: today });
    const summary = data?.data || data?.stats || data;
    return {
      type: "stats",
      title: "💰 Today's Collections",
      cards: [
        { label: "Lab Collection", value: summary?.lab_collection ? `₹${Number(summary.lab_collection).toLocaleString("en-IN")}` : "—" },
        { label: "OPD Collection", value: summary?.opd_collection ? `₹${Number(summary.opd_collection).toLocaleString("en-IN")}` : "—" },
        { label: "IPD Collection", value: summary?.ipd_collection ? `₹${Number(summary.ipd_collection).toLocaleString("en-IN")}` : "—" },
        { label: "Pharmacy", value: summary?.pharmacy_collection ? `₹${Number(summary.pharmacy_collection).toLocaleString("en-IN")}` : "—" },
        { label: "Total", value: summary?.total ? `₹${Number(summary.total).toLocaleString("en-IN")}` : "—" },
      ].filter(c => c.value !== "—"),
      text: "Here's today's collection breakdown by department."
    };
  } catch (err) {
    console.warn("[QuickAction] getTodayCollections error:", err.message);
    return null;
  }
};

export const getExpiringMedicines = async () => {
  try {
    const data = await postForm(AppConst.getExpiringMedicinesUrl, {});
    const list = extractList(data);
    if (list.length === 0) return { type: "text", text: "No medicines expiring soon. ✅" };
    return {
      type: "table",
      title: `⚠️ Expiring Medicines (${list.length})`,
      columns: ["#", "Medicine", "Batch", "Expiry", "Qty"],
      rows: list.slice(0, 15).map((m, i) => [
        i + 1,
        m.medicine_name || m.med_name || m.name || "—",
        m.batch_no || m.batch || "—",
        m.expiry_date || m.expiry || "—",
        m.quantity || m.qty || m.stock || "—"
      ]),
      text: list.length > 15 ? `Showing first 15 of ${list.length} items.` : ""
    };
  } catch (err) {
    console.warn("[QuickAction] getExpiringMedicines error:", err.message);
    return null;
  }
};

export const getDoctorsList = async () => {
  try {
    const data = await postForm(AppConst.getUserListUrl, { page_no: "none", search_key: "" });
    const list = extractList(data);
    const doctors = list.filter(u =>
      u.role === "doctor" || u.role_name === "doctor" ||
      u.user_type === "doctor" || u.designation === "Doctor"
    );
    const finalList = doctors.length > 0 ? doctors : list;
    if (finalList.length === 0) return { type: "text", text: "No doctors found in the system." };
    return {
      type: "table",
      title: `👨‍⚕️ Doctors (${finalList.length})`,
      columns: ["#", "Name", "Speciality", "Department", "Mobile"],
      rows: finalList.slice(0, 20).map((d, i) => [
        i + 1,
        d.name || (d.fname ? `${d.fname || ""} ${d.lname || ""}`.trim() : d.doctor_name) || "—",
        d.speciality || d.specialization || "—",
        d.department || d.dept_name || "—",
        d.mobile || d.phone || "—"
      ]),
      text: ""
    };
  } catch (err) {
    console.warn("[QuickAction] getDoctorsList error:", err.message);
    return null;
  }
};

/* ------------------------------------------------------------------ */
/* UHID detection + keyword sets                                       */
/* ------------------------------------------------------------------ */

const UHID_REGEX = /\b([a-z]{2,}\d{4,})\b/i;
const LAB_WORDS = ["lab", "test", "report", "sample", "pathology", "investigation"];
const IPD_WORDS = [
  "ipd", "admit", "admitted", "admission", "moved", "move",
  "transfer", "transferred", "shifted", "shift", "ward", "bed",
];
const OPD_WORDS = ["opd", "consult", "consultation", "outpatient"];

/* ------------------------------------------------------------------ */
/* INTENT MATCHER                                                      */
/* ------------------------------------------------------------------ */

export const matchQuickAction = async (message) => {
  const msg = message.toLowerCase().trim();

  const uhidHit = message.match(UHID_REGEX);
  if (uhidHit) {
    const uhid = uhidHit[1];
    const wantsLab = LAB_WORDS.some(w => msg.includes(w));
    const wantsIpd = IPD_WORDS.some(w => msg.includes(w));
    const wantsOpd = OPD_WORDS.some(w => msg.includes(w));
    let result;
    if (wantsLab)      result = await getLabStatus(uhid);
    else if (wantsIpd) result = await getPatientStatus(uhid);
    else if (wantsOpd) result = await getOpdStatus(uhid);
    else               result = await searchPatient(uhid, "uhid");
    if (result) return { matched: true, result };
  }

  if (msg.includes("dashboard") || msg.includes("today's stats") ||
      msg.includes("today stats") || msg.includes("overview") ||
      msg.includes("summary today")) {
    const result = await getDashboardStats();
    if (result) return { matched: true, result };
  }

  if (msg.includes("opd patient") || msg.includes("opd list") ||
      msg.includes("opd count") || msg.includes("how many opd") ||
      msg.includes("today opd") || msg.includes("today's opd")) {
    const result = await getOpdPatients();
    if (result) return { matched: true, result };
  }

  if (msg.includes("ipd patient") || msg.includes("ipd list") ||
      msg.includes("ipd admission") || msg.includes("admitted patient") ||
      msg.includes("how many ipd") || msg.includes("ipd count")) {
    const result = await getIpdPatients();
    if (result) return { matched: true, result };
  }

  if (msg.includes("bed") || msg.includes("available bed") ||
      msg.includes("bed availability") || msg.includes("bed status") ||
      msg.includes("vacant bed") || msg.includes("free bed")) {
    const result = await getAvailableBeds();
    if (result) return { matched: true, result };
  }

  if (msg.includes("lab report") || msg.includes("pending report") ||
      msg.includes("pending lab") || msg.includes("lab status") ||
      msg.includes("test report")) {
    const result = await getPendingLabReports();
    if (result) return { matched: true, result };
  }

  if (msg.includes("collection") || msg.includes("revenue") ||
      msg.includes("earning") || msg.includes("today's collection") ||
      msg.includes("total collection") || msg.includes("income")) {
    const result = await getTodayCollections();
    if (result) return { matched: true, result };
  }

  const namePatterns = [/search\s+patient\s+(.+)/i, /find\s+patient\s+(.+)/i];
  for (const pattern of namePatterns) {
    const m = message.match(pattern);
    if (m) {
      const query = (m[1] || "").trim();
      if (query && query.length >= 2) {
        const result = await searchPatient(query, "name");
        if (result) return { matched: true, result };
      }
    }
  }

  if (msg.includes("expir") || msg.includes("medicine expir") ||
      msg.includes("stock expir") || msg.includes("expired medicine")) {
    const result = await getExpiringMedicines();
    if (result) return { matched: true, result };
  }

  if (msg.includes("doctor list") || msg.includes("list doctor") ||
      msg.includes("show doctor") || msg.includes("all doctor") ||
      msg.includes("how many doctor")) {
    const result = await getDoctorsList();
    if (result) return { matched: true, result };
  }

  return { matched: false };
};

export default {
  getCurrentUser,
  getDashboardStats,
  getOpdPatients,
  getIpdPatients,
  getAvailableBeds,
  getPendingLabReports,
  searchPatient,
  searchPatientRaw,
  getPatientStatus,
  getLabStatus,
  getOpdStatus,
  getTodayCollections,
  getExpiringMedicines,
  getDoctorsList,
  matchQuickAction,
};